import AppBar from '@material-ui/core/AppBar';
import HeaderHelp from './HeaderHelp';
import HeaderLogin from './HeaderLogin';
import HeaderMap from './HeaderMap';
import HeaderMembership from './HeaderMembership';
import HeaderNotifications from './HeaderNotifications';
import GiftAnnouncement from './Announcements/GiftAnnouncement';
import DEIAnnouncement from './Announcements/DEIAnnouncement';
import HeaderSignup from './HeaderSignup';
import HeaderUser from './HeaderUser';
import Menu from '@material-ui/core/Menu';
import MenuIcon from '@material-ui/icons/Menu';
import React, {useCallback, useState} from 'react';
import theme from '../../theme';
import Toolbar from '@material-ui/core/Toolbar';
import Divider from '@material-ui/core/Divider';
import {GRANITE_2} from '../../colors';
import trackAnalytics from '../../utils/gaia/trackAnalytics';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useUser from '../../hooks/useUser';
import AuthModal, {AuthModalState} from '../AuthModal';

function isDecember() {
    const d = new Date();
    const month = d.getMonth();
    const december = 11;
    return month === december;
}

const commonButtonStyle: React.CSSProperties = {
    marginLeft: theme.spacing(2),
    fontSize: 14,
    outline: 0,
};

const commonLinkStyle: React.CSSProperties = {
    outline: 0,
    border: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: 'flex',
    borderBottomColor: theme.palette.primary.main,
    borderBottomStyle: 'solid',
    alignItems: 'center',
    color: GRANITE_2,
    textDecoration: 'none',
    fontSize: 14,
};

export default function Header() {
    const [user] = useUser();
    const [authModal, setAuthModal] = useState<AuthModalState>({
        form: undefined,
        analyticsContext: undefined,
    });
    const setForm = useCallback(
        (form: AuthModalState['form']) => {
            setAuthModal({...authModal, form});
        },
        [setAuthModal, authModal]
    );

    const [anchorEl, setAnchorEl] = useState<HTMLAnchorElement | null>(null);

    const isHamburgerVisible = useMediaQuery(theme.breakpoints.down('sm'));
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));

    const closeMenu = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);
    const openMenu = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
        },
        [setAnchorEl]
    );

    const linkStyle = {
        ...commonLinkStyle,
        display: isHamburgerVisible ? 'none' : 'flex',
    };

    const buttonStyle = {
        ...commonButtonStyle,
        display: isHamburgerVisible ? 'none' : 'initial',
    };

    return (
        <>
            <AppBar
                elevation={0}
                color='default'
                position='static'
                style={{boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'}}
            >
                <Toolbar style={{boxSizing: 'border-box', height: 64}}>
                    <a
                        href='/'
                        style={{
                            // eslint-disable-next-line max-len
                            background: `url(${Gaia.staticURL}img/logos_icons/gaia_gps_logo.svg) left center no-repeat`,
                            width: 200,
                            height: 41,
                            backgroundSize: '200px 41px',
                            marginRight: 18,
                            marginLeft: 0,
                            marginBottom: 0,
                        }}
                    />

                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'stretch',
                            alignSelf: 'stretch',
                        }}
                    >
                        <HeaderMap.Toolbar style={linkStyle} />
                        <HeaderHelp.Toolbar style={linkStyle} user={user} />
                        <HeaderMembership.Toolbar
                            style={linkStyle}
                            user={user}
                        />
                    </div>

                    {!isXs && <AppIcons user={user} />}

                    <HeaderLogin.Toolbar
                        style={buttonStyle}
                        user={user}
                        setAuthModal={setAuthModal}
                    />
                    <HeaderSignup.Toolbar
                        style={buttonStyle}
                        user={user}
                        setAuthModal={setAuthModal}
                    />
                    <HeaderNotifications.Toolbar
                        style={linkStyle}
                        user={user}
                    />
                    <HeaderUser.Toolbar style={linkStyle} user={user} />

                    {isHamburgerVisible && (
                        <a
                            style={{...commonLinkStyle, paddingRight: 0}}
                            href='#'
                            onClick={openMenu}
                        >
                            <MenuIcon />
                        </a>
                    )}

                    <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={closeMenu}
                    >
                        <HeaderMap.HamburgerMenu />
                        <HeaderHelp.HamburgerMenu user={user} />
                        <HeaderMembership.HamburgerMenu user={user} />
                        <Divider />
                        <HeaderLogin.HamburgerMenu user={user} />
                        <HeaderSignup.HamburgerMenu user={user} />
                        <HeaderNotifications.HamburgerMenu user={user} />
                        <HeaderUser.HamburgerMenu user={user} />
                    </Menu>
                </Toolbar>
            </AppBar>
            {isDecember() && <GiftAnnouncement />}
            <DEIAnnouncement />
            <AuthModal setForm={setForm} {...authModal} />
        </>
    );
}

function AppIcons({user}: {user: User | undefined}) {
    if (!user || !user.isAuthenticated || user.hasMobileApp) return null;

    const style = {...commonButtonStyle, height: 34, verticalAlign: 'middle'};

    return (
        <>
            <a
                href={
                    'https://itunes.apple.com/us/app/gaia-gps/id1201979492?mt=8&uo=4'
                }
                target='itunes_store'
                onClick={() =>
                    trackAnalytics('Buy iOS', {
                        category: 'Commerce',
                        label: 'Header',
                        page: window.location.href,
                    })
                }
            >
                <img
                    style={style}
                    src={`${Gaia.staticURL}img/appstore.svg`}
                    alt='Download on the App Store'
                />
            </a>

            <a
                href='https://market.android.com/details?id=com.trailbehind.android.gaiagps.pro&hl=en'
                target='_blank'
                rel='noopener noreferrer'
                onClick={() =>
                    trackAnalytics('Buy Android', {
                        category: 'Commerce',
                        label: 'Header',
                        page: window.location.href,
                    })
                }
            >
                <img
                    style={style}
                    src={`${Gaia.staticURL}img/icons/play_badge.png`}
                    alt='Get it on Google Play'
                />
            </a>
        </>
    );
}
